import { useState, useEffect, useRef } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const isCancel = location.pathname.includes("cancel");
  const [status, setStatus] = useState(isCancel ? "cancel" : "checking");
  const [info, setInfo] = useState(null);
  const [jar, setJar] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          if (data.tip_type === "community") api.get("/jar").then((r) => setJar(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired" || attempts.current > 12) { setStatus("timeout"); return; }
      } catch { if (attempts.current > 12) { setStatus("timeout"); return; } }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, isCancel]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="bg-card rounded-3xl border border-border p-10 max-w-md w-full text-center shadow-lg" data-testid="payment-result">
        {status === "checking" && (<>
          <Loader2 className="w-14 h-14 text-copper animate-spin mx-auto" />
          <h1 className="font-serif text-2xl font-bold text-bronze mt-4">Confirming your gift…</h1>
          <p className="text-muted-foreground mt-2">One moment while we drop your penny in.</p>
        </>)}
        {status === "success" && (<>
          <div className="relative"><CheckCircle2 className="w-16 h-16 text-sage mx-auto coin-drop" /></div>
          <h1 className="font-serif text-3xl font-bold text-bronze mt-4">Thank you! 💛</h1>
          <p className="text-muted-foreground mt-2">
            {info?.tip_type === "shop"
              ? "Your goodies are ready — find them in the Community Room and your sticker picker."
              : info?.tip_type === "group_fund"
              ? <>Your ${info?.amount?.toFixed(2)} is now in your Neighbors group fund. Together you're making a real difference. 💛</>
              : <>Your ${info?.amount?.toFixed(2)} {info?.tip_type === "community" ? "is now in the community jar" : "went straight to their wallet"}. Small kindness, big comfort.</>}
          </p>
          {info?.tip_type === "community" && jar && (
            <div data-testid="jar-confirmation" className="mt-5 bg-gradient-to-br from-penny/15 to-copper/10 border border-penny/25 rounded-2xl p-5">
              <p className="font-mono text-[11px] uppercase tracking-widest text-copper font-bold">The jar now holds</p>
              <p data-testid="jar-new-balance" className="font-serif text-4xl font-extrabold text-bronze mt-0.5">${(jar.balance || 0).toFixed(2)}</p>
              <p className="text-sm text-foreground/70 mt-1">to be shared equally with <b className="text-bronze">{jar.seeker_count}</b> {jar.seeker_count === 1 ? "neighbour" : "neighbours"} at the next daily split. 💛</p>
            </div>
          )}
          <div className="flex gap-2 mt-6">
            {info?.tip_type === "shop"
              ? <Button onClick={() => navigate("/community")} className="flex-1 bg-copper hover:bg-copper/90 text-white rounded-full">Open Community</Button>
              : <Button onClick={() => navigate("/discover")} className="flex-1 bg-copper hover:bg-copper/90 text-white rounded-full">Keep giving</Button>}
            <Button onClick={() => navigate("/")} variant="outline" className="flex-1 rounded-full">Home</Button>
          </div>
        </>)}
        {(status === "cancel" || status === "timeout") && (<>
          <XCircle className="w-16 h-16 text-muted-foreground mx-auto" />
          <h1 className="font-serif text-2xl font-bold text-bronze mt-4">{status === "cancel" ? "Payment cancelled" : "Still processing"}</h1>
          <p className="text-muted-foreground mt-2">{status === "cancel" ? "No worries — you can give anytime." : "Your payment may still complete. Check your wallet shortly."}</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-copper hover:bg-copper/90 text-white rounded-full w-full">Back home</Button>
        </>)}
      </div>
    </div>
  );
}
